import { useDispatch, useSelector } from 'react-redux';

import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';

import { tokenexApi } from './api/3rdparty/tokenex';
import { accountApi } from './api/account';
import { accountSettingsApi } from './api/accountSettings';
import { appSettingsApi } from './api/appSettings';
import { authenticationApi } from './api/authentication';
import { avatarProfileApi } from './api/avatarProfileApi';
import { categoryApi } from './api/category';
import { categoryDetailsApi } from './api/categoryDetails';
import { checkoutApi } from './api/checkout';
import { checkoutExperienceApi } from './api/checkoutExperience';
import { commissionsApi } from './api/commissions';
import { contactUsApi } from './api/ContactUs';
import { customerApi } from './api/customer';
import { customerAgainstWebAliasApi } from './api/customerAgainstWebAlias';
import { dashboardApi } from './api/dashboard';
import { dashboardStrapiApi } from './api/dashboardStrapi';
import { enrollmentBYPApi } from './api/enrollmentBYP';
import { enrollmentFlowApi } from './api/enrollmentFlow';
import { ezShipApi } from './api/ezShip';
import { ezshipManagerStrapiApi } from './api/ezshipManagerStrapi';
import { foodLabelsApi } from './api/foodLabels';
import { giftApi } from './api/gift';
import { interstitialApi } from './api/interstitialPopupApi';
import { leadGenerationApi } from './api/leadGeneration';
import { metaDataApi } from './api/metaData';
import { newsletterApi } from './api/newsletter';
import { orderApi } from './api/orders';
import { pawtripApi } from './api/pawtrip';
import { paymentMethodsApi } from './api/paymentMethod';
import { pdfApi } from './api/pdf';
import { petProfileApi } from './api/petProfile';
import { popupApi } from './api/popup';
import { productApi } from './api/product';
import { productIngredientsApi } from './api/productIngredients';
import { productReviewsApi } from './api/productReviews';
import { recentSearchApi } from './api/recentSearch';
import { recommendationPagesApi } from './api/recommendationPages';
import { reportsApi } from './api/reports';
import { searchApi } from './api/search';
import { shoppingCartApi } from './api/shoppingCart';
import { toolsApi } from './api/tools';
import { twoFactorAuthenticationApi } from './api/twoFactorAuthentication';
import { addressApi } from './api/user/account/address';
import { paymentApi } from './api/user/account/payment';
import { rtkQueryErrorLogger } from './middleware/errorLogger';
import appConfig from './slices/appConfig';
import authenticationSlice from './slices/authentication';
import categoryDetails from './slices/categoryDetails';
import CategoryProductStatsSlice from './slices/categoryProductStats';
import dashboard from './slices/dashboard';
import enrollmentSlice from './slices/enrollment';
import enrollmentCheckoutSlice from './slices/enrollmentCheckout';
import ezShipSlice from './slices/ezship';
import forgotPasswordSlice from './slices/forgotPasswordSlice';
import navigationSlice from './slices/navigationSlice';
import petLink from './slices/petLink';
import petProfileSlice from './slices/petProfile';
import petProfileUI from './slices/petProfileUI';
import petRecommendationsSlice from './slices/petRecommendations';
import recentSearchSlice from './slices/recentSearch';
import searchSlice from './slices/search';
import tokenexSlice from './slices/tokenex';
import webaliasSlice from './slices/webalias';
import type { TypedUseSelectorHook } from 'react-redux';

export const reducers = {
  [ezShipSlice.name]: ezShipSlice,
  [petProfileSlice.name]: petProfileSlice,
  [navigationSlice.name]: navigationSlice,
  [petRecommendationsSlice.name]: petRecommendationsSlice,
  [enrollmentSlice.name]: enrollmentSlice,
  [searchSlice.name]: searchSlice,
  [authenticationSlice.name]: authenticationSlice,
  [CategoryProductStatsSlice.name]: CategoryProductStatsSlice,
  [webaliasSlice.name]: webaliasSlice,
  [tokenexSlice.name]: tokenexSlice,
  [appConfig.name]: appConfig,
  [petProfileUI.name]: petProfileUI,
  [petLink.name]: petLink,
  [enrollmentCheckoutSlice.name]: enrollmentCheckoutSlice,
  [recentSearchSlice.name]: recentSearchSlice,
  [categoryDetails.name]: categoryDetails,
  [dashboard.name]: dashboard,
  [forgotPasswordSlice.name]: forgotPasswordSlice,
};

const combinedReducers = combineReducers({
  [ezShipSlice.name]: ezShipSlice.reducer,
  [petProfileSlice.name]: petProfileSlice.reducer,
  [navigationSlice.name]: navigationSlice.reducer,
  [petRecommendationsSlice.name]: petRecommendationsSlice.reducer,
  [enrollmentSlice.name]: enrollmentSlice.reducer,
  [searchSlice.name]: searchSlice.reducer,
  [authenticationSlice.name]: authenticationSlice.reducer,
  [CategoryProductStatsSlice.name]: CategoryProductStatsSlice.reducer,
  [webaliasSlice.name]: webaliasSlice.reducer,
  [tokenexSlice.name]: tokenexSlice.reducer,
  [appConfig.name]: appConfig.reducer,
  [petProfileUI.name]: petProfileUI.reducer,
  [petLink.name]: petLink.reducer,
  [enrollmentCheckoutSlice.name]: enrollmentCheckoutSlice.reducer,
  [recentSearchSlice.name]: recentSearchSlice.reducer,
  [categoryDetails.name]: categoryDetails.reducer,
  [dashboard.name]: dashboard.reducer,
  [forgotPasswordSlice.name]: forgotPasswordSlice.reducer,
});

export const slices = {
  [metaDataApi.reducerPath]: metaDataApi,
  [popupApi.reducerPath]: popupApi,
  [dashboardStrapiApi.reducerPath]: dashboardStrapiApi,
  [productApi.reducerPath]: productApi,
  [toolsApi.reducerPath]: toolsApi,
  [reportsApi.reducerPath]: reportsApi,
  [categoryDetailsApi.reducerPath]: categoryDetailsApi,
  [categoryApi.reducerPath]: categoryApi,
  [searchApi.reducerPath]: searchApi,

  [shoppingCartApi.reducerPath]: shoppingCartApi,
  [paymentApi.reducerPath]: paymentApi,
  [addressApi.reducerPath]: addressApi,
  [tokenexApi.reducerPath]: tokenexApi,
  [checkoutApi.reducerPath]: checkoutApi,
  [newsletterApi.reducerPath]: newsletterApi,
  [foodLabelsApi.reducerPath]: foodLabelsApi,
  [customerApi.reducerPath]: customerApi,
  [customerAgainstWebAliasApi.reducerPath]: customerAgainstWebAliasApi,
  [ezShipApi.reducerPath]: ezShipApi,
  [accountApi.reducerPath]: accountApi,
  [authenticationApi.reducerPath]: authenticationApi,
  [twoFactorAuthenticationApi.reducerPath]: twoFactorAuthenticationApi,
  [productReviewsApi.reducerPath]: productReviewsApi,
  [leadGenerationApi.reducerPath]: leadGenerationApi,
  [pdfApi.reducerPath]: pdfApi,
  [contactUsApi.reducerPath]: contactUsApi,
  [petProfileApi.reducerPath]: petProfileApi,
  [recommendationPagesApi.reducerPath]: recommendationPagesApi,
  [orderApi.reducerPath]: orderApi,
  [accountSettingsApi.reducerPath]: accountSettingsApi,
  [appSettingsApi.reducerPath]: appSettingsApi,
  [paymentMethodsApi.reducerPath]: paymentMethodsApi,
  [avatarProfileApi.reducerPath]: avatarProfileApi,
  [giftApi.reducerPath]: giftApi,
  [ezshipManagerStrapiApi.reducerPath]: ezshipManagerStrapiApi,
  [enrollmentBYPApi.reducerPath]: enrollmentBYPApi,
  [productIngredientsApi.reducerPath]: productIngredientsApi,
  [enrollmentFlowApi.reducerPath]: enrollmentFlowApi,
  [recentSearchApi.reducerPath]: recentSearchApi,
  [dashboardApi.reducerPath]: dashboardApi,
  [commissionsApi.reducerPath]: commissionsApi,
  [pawtripApi.reducerPath]: pawtripApi,
  [interstitialApi.reducerPath]: interstitialApi,
  [checkoutExperienceApi.reducerPath]: checkoutExperienceApi,
};

export const store = configureStore({
  reducer: {
    slices: combinedReducers,
    [metaDataApi.reducerPath]: metaDataApi.reducer,
    [popupApi.reducerPath]: popupApi.reducer,
    [dashboardStrapiApi.reducerPath]: dashboardStrapiApi.reducer,
    [productApi.reducerPath]: productApi.reducer,
    [toolsApi.reducerPath]: toolsApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [categoryDetailsApi.reducerPath]: categoryDetailsApi.reducer,
    [categoryApi.reducerPath]: categoryApi.reducer,
    [searchApi.reducerPath]: searchApi.reducer,
    [shoppingCartApi.reducerPath]: shoppingCartApi.reducer,
    [paymentApi.reducerPath]: paymentApi.reducer,
    [addressApi.reducerPath]: addressApi.reducer,
    [tokenexApi.reducerPath]: tokenexApi.reducer,
    [checkoutApi.reducerPath]: checkoutApi.reducer,
    [newsletterApi.reducerPath]: newsletterApi.reducer,
    [foodLabelsApi.reducerPath]: foodLabelsApi.reducer,
    [customerApi.reducerPath]: customerApi.reducer,
    [customerAgainstWebAliasApi.reducerPath]: customerAgainstWebAliasApi.reducer,
    [ezShipApi.reducerPath]: ezShipApi.reducer,
    [accountApi.reducerPath]: accountApi.reducer,
    [authenticationApi.reducerPath]: authenticationApi.reducer,
    [twoFactorAuthenticationApi.reducerPath]: twoFactorAuthenticationApi.reducer,
    [productReviewsApi.reducerPath]: productReviewsApi.reducer,
    [leadGenerationApi.reducerPath]: leadGenerationApi.reducer,
    [pdfApi.reducerPath]: pdfApi.reducer,
    [contactUsApi.reducerPath]: contactUsApi.reducer,
    [petProfileApi.reducerPath]: petProfileApi.reducer,
    [recommendationPagesApi.reducerPath]: recommendationPagesApi.reducer,
    [orderApi.reducerPath]: orderApi.reducer,
    [accountSettingsApi.reducerPath]: accountSettingsApi.reducer,
    [appSettingsApi.reducerPath]: appSettingsApi.reducer,
    [paymentMethodsApi.reducerPath]: paymentMethodsApi.reducer,
    [avatarProfileApi.reducerPath]: avatarProfileApi.reducer,
    [giftApi.reducerPath]: giftApi.reducer,
    [ezshipManagerStrapiApi.reducerPath]: ezshipManagerStrapiApi.reducer,
    [enrollmentBYPApi.reducerPath]: enrollmentBYPApi.reducer,
    [productIngredientsApi.reducerPath]: productIngredientsApi.reducer,
    [enrollmentFlowApi.reducerPath]: enrollmentFlowApi.reducer,
    [recentSearchApi.reducerPath]: recentSearchApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [commissionsApi.reducerPath]: commissionsApi.reducer,
    [pawtripApi.reducerPath]: pawtripApi.reducer,
    [interstitialApi.reducerPath]: interstitialApi.reducer,
    [checkoutExperienceApi.reducerPath]: checkoutExperienceApi.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware()
      .concat(metaDataApi.middleware)
      .concat(popupApi.middleware)
      .concat(dashboardStrapiApi.middleware)
      .concat(categoryDetailsApi.middleware)
      .concat(categoryApi.middleware)
      .concat(searchApi.middleware)
      .concat(productApi.middleware)
      .concat(toolsApi.middleware)
      .concat(reportsApi.middleware)
      .concat(shoppingCartApi.middleware)
      .concat(paymentApi.middleware)
      .concat(addressApi.middleware)
      .concat(tokenexApi.middleware)
      .concat(checkoutApi.middleware)
      .concat(foodLabelsApi.middleware)
      .concat(newsletterApi.middleware)
      .concat(customerApi.middleware)
      .concat(customerAgainstWebAliasApi.middleware)
      .concat(ezShipApi.middleware)
      .concat(reportsApi.middleware)
      .concat(accountApi.middleware)
      .concat(authenticationApi.middleware)
      .concat(twoFactorAuthenticationApi.middleware)
      .concat(productReviewsApi.middleware)
      .concat(leadGenerationApi.middleware)
      .concat(pdfApi.middleware)
      .concat(contactUsApi.middleware)
      .concat(petProfileApi.middleware)
      .concat(recommendationPagesApi.middleware)
      .concat(orderApi.middleware)
      .concat(accountSettingsApi.middleware)
      .concat(appSettingsApi.middleware)
      .concat(paymentMethodsApi.middleware)
      .concat(avatarProfileApi.middleware)
      .concat(giftApi.middleware)
      .concat(ezshipManagerStrapiApi.middleware)
      .concat(enrollmentBYPApi.middleware)
      .concat(productIngredientsApi.middleware)
      .concat(enrollmentFlowApi.middleware)
      .concat(recentSearchApi.middleware)
      .concat(dashboardApi.middleware)
      .concat(commissionsApi.middleware)
      .concat(pawtripApi.middleware)
      .concat(interstitialApi.middleware)
      .concat(checkoutExperienceApi.middleware)
      .concat(rtkQueryErrorLogger),
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
